import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { signalsAPI, subscriptionAPI } from '../services/api'

interface Anomaly {
  type: string
  severity: number
  headline: string
  direction_symbol: string
  detail?: string
}

interface Signal {
  id: string
  symbol: string
  signal_type: string
  anomaly_score: number
  generated_at: string
  details: {
    spot: number
    atm_iv: number
    pcr: number
    anomalies: Anomaly[]
    strategy_hint?: string
  }
}

export default function Dashboard() {
  const [signals, setSignals] = useState<Signal[]>([])
  const [plan, setPlan] = useState('basic')
  const [nextScan, setNextScan] = useState('')
  const [loading, setLoading] = useState(true)
  const [lastUpdate, setLastUpdate] = useState('')
  const navigate = useNavigate()

  const fetchSignals = async () => {
    try {
      const res = await signalsAPI.getLatest()
      setSignals(res.data.signals || [])
      setPlan(res.data.plan || 'basic')
      setNextScan(res.data.next_scan || '')
      setLastUpdate(new Date().toLocaleTimeString('en-IN'))
    } catch (err) {
      console.error('Failed to fetch signals', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSignals()
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchSignals, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  const logout = () => {
    localStorage.clear()
    navigate('/login')
  }

  const severityColor = (score: number) => {
    if (score >= 70) return '#ef4444'
    if (score >= 40) return '#f59e0b'
    return '#00d4ff'
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <span style={styles.logo}>AITradeMinds</span>
          <span style={styles.planBadge}>{plan.toUpperCase()}</span>
        </div>
        <div style={styles.headerRight}>
          {lastUpdate && (
            <span style={styles.updateTime}>Updated {lastUpdate}</span>
          )}
          <button style={styles.logoutBtn} onClick={logout}>Logout</button>
        </div>
      </div>

      {/* Stats bar */}
      <div style={styles.statsBar}>
        <div style={styles.stat}>
          <span style={styles.statValue}>{signals.length}</span>
          <span style={styles.statLabel}>Anomalies</span>
        </div>
        <div style={styles.stat}>
          <span style={styles.statValue}>203</span>
          <span style={styles.statLabel}>Stocks Scanned</span>
        </div>
        <div style={styles.stat}>
          <span style={styles.statValue}>{nextScan || '--:--'}</span>
          <span style={styles.statLabel}>Next Scan</span>
        </div>
        <div style={styles.stat}>
          <span style={styles.statValue}>15m</span>
          <span style={styles.statLabel}>Interval</span>
        </div>
      </div>

      {/* Signals */}
      <div style={styles.content}>
        {loading ? (
          <div style={styles.empty}>Loading signals...</div>
        ) : signals.length === 0 ? (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}>📊</div>
            <div>No anomalies detected in this scan</div>
            <div style={styles.emptyHint}>
              Market may be low-conviction or pipeline warming up
            </div>
            <div style={styles.emptyHint}>Next scan at {nextScan}</div>
          </div>
        ) : (
          <div style={styles.grid}>
            {signals.map(signal => (
              <div key={signal.id} style={styles.card}>
                <div style={styles.cardHeader}>
                  <span style={styles.symbol}>{signal.symbol}</span>
                  <span
                    style={{
                      ...styles.scoreBadge,
                      background: severityColor(signal.anomaly_score),
                    }}
                  >
                    {signal.anomaly_score}/100
                  </span>
                </div>

                <div style={styles.cardMeta}>
                  {signal.details?.spot > 0 && (
                    <span style={styles.metaItem}>
                      ₹{signal.details.spot.toLocaleString('en-IN')}
                    </span>
                  )}
                  {signal.details?.atm_iv > 0 && (
                    <span style={styles.metaItem}>
                      IV: {signal.details.atm_iv}%
                    </span>
                  )}
                  {signal.details?.pcr > 0 && (
                    <span style={styles.metaItem}>
                      PCR: {signal.details.pcr}
                    </span>
                  )}
                </div>

                {signal.details?.anomalies?.map((a, i) => (
                  <div key={i} style={styles.anomalyRow}>
                    <span style={styles.dirSymbol}>{a.direction_symbol}</span>
                    <div>
                      <div style={styles.anomalyType}>{a.type}</div>
                      <div style={styles.anomalyHeadline}>{a.headline}</div>
                      {a.detail && (
                        <div style={styles.anomalyDetail}>{a.detail}</div>
                      )}
                    </div>
                  </div>
                ))}

                {signal.details?.strategy_hint && (
                  <div style={styles.hint}>
                    💡 {signal.details.strategy_hint}
                  </div>
                )}

                <div style={styles.timestamp}>
                  {new Date(signal.generated_at).toLocaleTimeString('en-IN')}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={styles.disclaimer}>
        ⚠️ Research intelligence only. Not SEBI registered investment advice.
        Past anomaly patterns do not guarantee future results.
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: { background: '#0a0e1a', minHeight: '100vh', color: '#fff' },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 24px', borderBottom: '1px solid #1f2937',
    background: '#0d1117',
  },
  logo: { color: '#00d4ff', fontSize: 20, fontWeight: 700 },
  planBadge: {
    marginLeft: 12, background: '#1f2937', color: '#00d4ff',
    padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
  },
  headerRight: { display: 'flex', alignItems: 'center', gap: 16 },
  updateTime: { color: '#4b5563', fontSize: 12 },
  logoutBtn: {
    background: 'transparent', color: '#6b7280',
    border: '1px solid #374151', padding: '6px 16px',
    borderRadius: 6, cursor: 'pointer', fontSize: 13,
  },
  statsBar: {
    display: 'flex', gap: 0, borderBottom: '1px solid #1f2937',
    background: '#0d1117',
  },
  stat: {
    flex: 1, padding: '16px 24px', textAlign: 'center',
    borderRight: '1px solid #1f2937',
  },
  statValue: { display: 'block', fontSize: 24, fontWeight: 700, color: '#00d4ff' },
  statLabel: { display: 'block', fontSize: 11, color: '#6b7280', marginTop: 2 },
  content: { padding: 24 },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: 16,
  },
  card: {
    background: '#111827', border: '1px solid #1f2937',
    borderRadius: 12, padding: 20,
  },
  cardHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: 8,
  },
  symbol: { fontSize: 20, fontWeight: 700, color: '#fff' },
  scoreBadge: {
    padding: '4px 12px', borderRadius: 20,
    fontSize: 13, fontWeight: 700, color: '#000',
  },
  cardMeta: { display: 'flex', gap: 12, marginBottom: 16 },
  metaItem: { color: '#6b7280', fontSize: 13 },
  anomalyRow: {
    display: 'flex', gap: 12, alignItems: 'flex-start',
    padding: '10px 0', borderTop: '1px solid #1f2937',
  },
  dirSymbol: { fontSize: 20, minWidth: 24 },
  anomalyType: { color: '#00d4ff', fontSize: 11, fontWeight: 700, marginBottom: 2 },
  anomalyHeadline: { color: '#e5e7eb', fontSize: 13 },
  anomalyDetail: { color: '#6b7280', fontSize: 12, marginTop: 4 },
  hint: {
    background: '#1f2937', borderRadius: 8,
    padding: '10px 12px', fontSize: 13, color: '#9ca3af', marginTop: 12,
  },
  timestamp: { color: '#374151', fontSize: 11, marginTop: 12 },
  empty: {
    textAlign: 'center', padding: '80px 20px',
    color: '#6b7280', fontSize: 16,
  },
  emptyIcon: { fontSize: 48, marginBottom: 16 },
  emptyHint: { fontSize: 13, color: '#4b5563', marginTop: 8 },
  disclaimer: {
    textAlign: 'center', padding: '16px',
    color: '#374151', fontSize: 11,
    borderTop: '1px solid #1f2937',
  },
}